import * as React from "react";
import { useAtomValue, useStore } from "jotai";

import {
  expandedStopRootsAtom,
  stopLocationsByRootAtom,
  stopLocationsInflightAtom,
} from "../atoms";

import { fetchLocationsForRoot } from "../api/stopLocations";
import { getActiveTpid } from "../api/timetablePeriods";
import type { Stop } from "../types";

const keyOf = (tpid: string, rootId: string) => `${tpid}|${rootId}`;

export function useStopLocations() {
  const store = useStore();

  const expanded = useAtomValue(expandedStopRootsAtom);
  const cache = useAtomValue(stopLocationsByRootAtom);

  const periodRequestRef = React.useRef<Promise<string> | null>(null);

  const resolveTpid = React.useCallback(
    (requestedTpid?: string): Promise<string> => {
      if (requestedTpid && requestedTpid !== "auto") {
        return Promise.resolve(requestedTpid);
      }

      if (periodRequestRef.current) {
        return periodRequestRef.current;
      }

      const request = getActiveTpid()
        .then((tpid) => {
          if (tpid == null) {
            throw new Error("Ingen aktiv tidtabellsperiod hittades");
          }

          return String(tpid);
        })
        .finally(() => {
          if (periodRequestRef.current === request) {
            periodRequestRef.current = null;
          }
        });

      periodRequestRef.current = request;
      return request;
    },
    []
  );

  const ensureLoaded = React.useCallback(
    async (rootId: string, requestedTpid?: string): Promise<Stop[]> => {
      const tpid = await resolveTpid(requestedTpid);
      const key = keyOf(tpid, rootId);

      const cached = store.get(stopLocationsByRootAtom).get(key);

      if (cached) {
        return cached.stops;
      }

      const running = store.get(stopLocationsInflightAtom).get(key);

      if (running) {
        return running;
      }

      const request = fetchLocationsForRoot(rootId, tpid)
        .then((stops) => {
          store.set(stopLocationsByRootAtom, (previous) => {
            const next = new Map(previous);
            next.set(key, { tpid, stops });
            return next;
          });

          return stops;
        })
        .finally(() => {
          store.set(stopLocationsInflightAtom, (previous) => {
            if (!previous.has(key)) return previous;

            const next = new Map(previous);
            next.delete(key);
            return next;
          });
        });

      store.set(stopLocationsInflightAtom, (previous) => {
        const next = new Map(previous);
        next.set(key, request);
        return next;
      });

      return request;
    },
    [store, resolveTpid]
  );

  const toggleRoot = React.useCallback(
    async (rootId: string, checked: boolean): Promise<void> => {
      store.set(expandedStopRootsAtom, (previous) => {
        if (previous.has(rootId) === checked) return previous;

        const next = new Set(previous);

        if (checked) {
          next.add(rootId);
        } else {
          next.delete(rootId);
        }

        return next;
      });

      if (checked) {
        await ensureLoaded(rootId);
      }
    },
    [store, ensureLoaded]
  );

  const [automaticTpid, setAutomaticTpid] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    void resolveTpid()
      .then((tpid) => {
        if (!cancelled) {
          setAutomaticTpid(tpid);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.error("Kunde inte läsa tidtabellsperiod för lägen", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolveTpid]);

  const getLocationsFor = React.useCallback(
    (rootId: string, preferTpid?: string): Stop[] => {
      const tpid =
        preferTpid && preferTpid !== "auto" ? preferTpid : automaticTpid;

      if (!tpid) return [];

      return cache.get(keyOf(tpid, rootId))?.stops ?? [];
    },
    [cache, automaticTpid]
  );

  return {
    expanded,
    toggleRoot,
    ensureLoaded,
    getLocationsFor,
    resolveTpid,
  };
}
